import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { useEventDispatcher } from '../../../eventing/components/eventing-service-provider/eventing-service-provider';
import { EventName } from '../../../eventing/interfaces/events.interface';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Radio } from '../../../shared/components/radio/radio';
import { useToast } from '../../../shared/hooks/use-toast';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { getErrorMessage } from '../../../shared/utils/convert-error';
import type { IUserSelfExcludeLimit } from '../../limits.interface';
import { UserLimitPeriod } from '../../limits.interface';
import {
    ICreatorName,
    ISuspensionLimitsType,
    useAddUserSuspensionLimitMutation,
} from '../../queries/suspension-limit.graphql';
import { FormSection } from '../form-section/form-section';

import styles from './self-exclusion-form.module.scss';

const style = createStyleHelper(styles, 'self-exclusion-form');

export const SelfExclusionForm: FunctionComponent = () => {
    const intl = useIntl();
    const auth = useAuth();
    const toast = useToast();
    const dispatchEvent = useEventDispatcher();
    const profileId = auth.user?.profile?.user_profile_id as string;

    const {
        register,
        handleSubmit,
        formState: { isDirty, isValid },
        reset,
    } = useForm<IUserSelfExcludeLimit>({ mode: 'onChange' });

    const [{ fetching }, execute] = useAddUserSuspensionLimitMutation();

    const onSubmit = useCallback(
        async (limitData: IUserSelfExcludeLimit) => {
            const { error } = await execute({
                userProfileId: profileId,
                xTenantID: config.TENANT_ID,
                creatorName: ICreatorName.User,
                limitInfo: {
                    createdBy: profileId,
                    intent: ISuspensionLimitsType.Selfexclusion,
                    duration:
                        limitData.duration !== UserLimitPeriod.INDEFINITE
                            ? limitData.duration
                            : null,
                },
            });

            if (error) {
                toast.error(getErrorMessage(intl, 'user.limits.create', error));
                return;
            }

            dispatchEvent({
                name: EventName.RG_SUBMITTED,
                period: limitData.duration,
            });
            toast.success(
                intl.formatMessage({
                    id: 'user.limit.created.success',
                    defaultMessage: 'User Limit Created',
                }),
            );
            reset();
        },
        [execute, profileId, toast, intl, dispatchEvent, reset],
    );

    return (
        <form className={style()} onSubmit={handleSubmit(onSubmit)}>
            <FormSection
                title="limits.self-exclusion-form.title"
                description="self-exclusion-form.description"
                alwaysStacked
            >
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.P7D}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="self-exclusion-form.period.7-days"
                            defaultMessage="Don't allow me to login for the next 7 days"
                        />
                    }
                />
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.P3M}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="self-exclusion-form.period.3-months"
                            defaultMessage="Exclude me for the next 3 months"
                        />
                    }
                />
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.P6M}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="self-exclusion-form.period.6-months"
                            defaultMessage="Don't allow me to login for the next 6 months"
                        />
                    }
                />
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.INDEFINITE}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="self-exclusion-form.period.indefinite"
                            defaultMessage="Don't allow me to login indefinitely"
                        />
                    }
                />
            </FormSection>

            <div className={style('warning')}>
                <p>
                    <FormattedMessage
                        id="self-exclusion-form.warning"
                        defaultMessage="Pressing Confirm will automatically lead to logging you out of the casino for the time period you have chosen.<br></br>Rewards accumulated will be lost and all rewards will be reset."
                    />
                </p>

                <Button
                    className={style('submit')}
                    size={ButtonSize.LARGE}
                    type="submit"
                    loading={fetching}
                    disabled={!isDirty || !isValid}
                >
                    <FormattedMessage id="self-exclusion-form.submit" defaultMessage="Confirm" />
                </Button>
            </div>
        </form>
    );
};
